import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { useAtom } from 'jotai'
import { Button } from '../../ui/Button/Button'
import {
    SendFill,
    Info,
    SuitcaseFill,
    EditFill,
    IcAddJob,
} from '../../../assets/icons'
import { Circle } from '../../ui/Card'
import { CircleIcon } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import { IUpdateMemberData, Member } from '../../../models/Member'
import { Position } from '../../../models/Position'
import { getWorkplacesAndPositions } from '../../../api/misc'
import { colors } from '../../../theme/palette'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { baseButton } from '../../../theme/reuseables'
import { Overlay } from '../../ui/Sidebar'
import { Dialog } from '../../ui/Dialog/Dialog'
import { addMemberToWorkplace } from '../../../api/members/members'
import { Workplace } from '../../../models/Workplace'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import {
    FormInputNumber,
    FormTextArea,
    FormSelect,
} from '../FormElements/FormInput'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../providers/Lokalise'
import { Select } from '../../ui/FormElememts/Select'
import Checkbox from '../../ui/FormElememts/Checkbox'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'
import { profileAtom } from '../../../api/profile/profile'

interface EditProps {
    member: Member
    onOpen?: () => void
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    Timetal?: string
    OevrigeOplysninger?: string
    Arbejdsstednummer?: string
    Stillingsnummer?: string
}

export type CustomErrors = {
    Aendringsdato?: boolean
}

export const AddMemberToWorkplace: FC<EditProps> = ({ member, onOpen }) => {
    const { register, formState, reset, handleSubmit, setValue } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [workPlaces, setWorkplaces] = useState<Workplace[]>([])
    const [positions, setPositions] = useState<Position[]>([])
    const [selectedDate, _setSelectedDate] = useState(null)
    const [isOpen, setIsOpen] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [onCallSubstitute, setOnCallSubstitute] = useState<boolean>(false)
    const [customErrors, setCustomErrors] = useState<CustomErrors>({})
    const [values, setValues] = useState<RegistrationFormFields>({
        Timetal: undefined,
        OevrigeOplysninger: undefined,
    })
    const [selectedPostion, setSelectedPosition] = useState<string | undefined>(
        undefined
    )
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        string | undefined
    >(undefined)

    const getPositions = async (): Promise<void> => {
        const response = await getWorkplacesAndPositions()

        let workplaces = response.workPlaces

        workplaces = workplaces.filter((place) => {
            return member.workPlaces.some(
                (work) => work.number !== place.number
            )
        })

        setWorkplaces(workplaces)
    }

    useEffect(() => {
        getPositions()
    }, [])

    useEffect(() => {
        let valid = false

        valid =
            !!selectedDate &&
            !!selectedPostion &&
            selectedPostion !== 'default' &&
            !!selectedWorkplace
        setIsValid(valid)
    }, [values, selectedPostion, selectedWorkplace, selectedDate, formState])

    const setWP = (wp: string): void => {
        const found = workPlaces.find((wpp) => String(wpp.number) === wp)

        setSelectedWorkplace(wp)
        setSelectedPosition('default')

        if (found && found.positions) {
            setPositions(found.positions)
        }
    }

    const resetForm = (): void => {
        _setSelectedDate(null)
        setSelectedWorkplace('default')
        setSelectedPosition('default')
        setCustomErrors({})
        reset()
    }

    const { dirtyFields, errors } = formState

    const submitHandler = async (
        data: RegistrationFormFields
    ): Promise<string> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return Promise.resolve('OK')
        }
        const fieldsToEdit: IUpdateMemberData = {
            Medlemsident: member.memberId,
            Aendringsdato: moment(selectedDate).format('YYYY-MM-DD HH:mm:ss'),
            Arbejdsstednummer: Number(selectedWorkplace),
            Stillingsnummer: selectedPostion || String(member.positions[0].id),
        }
        if (dirtyFields.Timetal) {
            fieldsToEdit.Timetal = Number(
                String(data.Timetal).replace(',', '.')
            )
        }

        if (dirtyFields.OevrigeOplysninger) {
            fieldsToEdit.OevrigeOplysninger = data.OevrigeOplysninger
        }

        if (onCallSubstitute) {
            if (fieldsToEdit.OevrigeOplysninger) {
                fieldsToEdit.OevrigeOplysninger = `${fieldsToEdit.OevrigeOplysninger} - Tilkaldevikar`
            } else {
                fieldsToEdit.OevrigeOplysninger = 'Tilkaldevikar'
            }
        }

        const response = await addMemberToWorkplace(fieldsToEdit)

        if (response) {
            setIsOpen(false)
            setShowDialog(true)
            resetForm()
        }

        return Promise.resolve('OK')
    }

    return (
        <>
            <Button
                type="action"
                onClick={() => {
                    setIsOpen(true)
                    if (onOpen) {
                        onOpen()
                    }
                }}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignContent: 'start',
                    padding: 0,
                    justifyContent: 'left',
                }}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon iconWidth="22px">
                        <IcAddJob />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="XS" spacing=".2rem">
                    {L('MINEMEDLEMMER_DETALJE_TILFØJ-TIL-ARBEJDSPLADS_KNAP')}
                </Typography>
            </Button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                    resetForm()
                }}
            >
                <DrawerHeaderMemberInfo member={member} />
                <form
                    noValidate
                    className={classes.contentWrapper}
                    onSubmit={handleSubmit(submitHandler)}
                >
                    <FormHeader>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <div style={{ marginBottom: '1rem' }}>
                                <Circle marginRight=".5rem">
                                    <CircleIcon>
                                        <IcAddJob />
                                    </CircleIcon>
                                </Circle>
                            </div>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L_HTML(
                                    'MINEMEDLEMMER_DETALJE_TILFØJ-TIL-ARBEJDSPLADS_HEADER_1'
                                )}
                            </Typography>
                        </div>
                    </FormHeader>
                    <FormControl>
                        <FormSelect
                            register={register}
                            required
                            name="Arbejdsstednummer"
                            hasError={!!errors?.Arbejdsstednummer?.message}
                            rules={{
                                required: 'Arbejssted er påkrævet!',
                                onChange: (e) => {
                                    setWP(e.target.value)
                                    setValue(
                                        'Arbejdsstednummer',
                                        e.target.value
                                    )
                                },
                            }}
                            isOpen={isOpen}
                            placeholder="Arbejdssted *"
                            onChange={(e) => {
                                setWP(e.target.value)
                                setValue('Arbejdsstednummer', e.target.value)
                            }}
                            options={workPlaces.map((wp) => {
                                const o = {
                                    label: wp.name,
                                    value: String(wp.number),
                                }

                                return o
                            })}
                        />
                        <FormMessage
                            text=""
                            error={errors?.Arbejdsstednummer?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormSelect
                            register={register}
                            required
                            name="Stillingsnummer"
                            hasError={!!errors?.Stillingsnummer?.message}
                            rules={{
                                required: 'Stilling er påkrævet!',
                                onChange: (e) => {
                                    setSelectedPosition(e.target.value)
                                    setValue('Stillingsnummer', e.target.value)
                                },
                            }}
                            isOpen={isOpen}
                            disabled={!selectedWorkplace}
                            placeholder="Stilling *"
                            options={positions.map((pos) => {
                                const o = {
                                    label: pos.name,
                                    value: String(pos.id),
                                }

                                return o
                            })}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST')}
                            error={errors?.Stillingsnummer?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInputNumber
                            label={`${L(
                                'REDIGER-MEDLEM_FORM_TIMETAL_LABEL'
                            )} *`}
                            name="Timetal"
                            required={false}
                            useFloats
                            register={register}
                            defaultValue={undefined}
                            hasError={!!errors?.Timetal?.message}
                            rules={{
                                validate: (value) =>
                                    (Number(String(value).replace(',', '.')) >=
                                        0.5 &&
                                        Number(
                                            String(value).replace(',', '.')
                                        ) <= 99) ||
                                    L(
                                        'GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST'
                                    ),
                            }}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST')}
                            error={errors?.Timetal?.message}
                        />
                    </FormControl>

                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon>
                                <Info />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L_HTML(
                                'GENERISK_FORM_YDERLIGERE-OPLYSNINGER_TITEL'
                            )}
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <DatePickerComponent
                            label={`${L('GENERISK_FORM_ÆNDRES-PR_LABEL')}`}
                            selected={selectedDate}
                            setSelected={(e) => {
                                _setSelectedDate(e)
                                setCustomErrors({})
                            }}
                            hasError={!!customErrors?.Aendringsdato}
                            minDate={moment().subtract(1, 'year').toDate()}
                            maxDate={moment().add(1, 'year').toDate()}
                        />
                        <FormMessage
                            text=""
                            error={
                                customErrors.Aendringsdato
                                    ? 'Ændringsdato påkrævet'
                                    : undefined
                            }
                        />
                    </FormControl>
                    <FormControl>
                        <FormTextArea
                            label={L('GENERISK_FORM_ØVRIGE-OPLYSNINGER_LABEL')}
                            register={register}
                            name="OevrigeOplysninger"
                            required={false}
                        />
                    </FormControl>
                    <FormControl>
                        <Checkbox
                            label={L('GENERISK_FORM_TILKALDEVIKAR_LABEL')}
                            isChecked={onCallSubstitute}
                            onChange={() => {
                                setOnCallSubstitute(!onCallSubstitute)
                            }}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                resetForm()
                            }}
                            type="secondary"
                            size="large"
                        >
                            Annuller
                        </Button>
                        <Button
                            buttonType="submit"
                            type="primary"
                            size="large"
                            onClick={() => {
                                if (!selectedDate) {
                                    setCustomErrors({
                                        Aendringsdato: true,
                                    })
                                }
                            }}
                        >
                            {L_HTML(
                                'MINEMEDLEMMER_TILFØJ-ARBEJDSSTED_TILFØJ_LABEL'
                            )}
                        </Button>
                    </div>

                    <div />
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<SendFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L_HTML(
                            'TILFØJ-MEDLEM-TIL-ARBEJDSPLADS_POPUP_OVERSKRIFT'
                        )}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L_HTML('TILFØJ-MEDLEM-TIL-ARBEJDSPLADS_POPUP_TEXT')}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {L_HTML('TILFØJ-MEDLEM-TIL-ARBEJDSPLADS_POPUP_KNAP')}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}
